// @mui
import { Stack, Typography, Box, CircularProgress } from '@mui/material';
import { Iconify } from '@pflegenavi/web-components';
import { useTranslation } from 'react-i18next';
import { useFormatting } from '@pflegenavi/frontend/localization';

import type { JSX } from 'react';

// ----------------------------------------------------------------------

interface Props {
  icon: string | React.ReactNode;
  title: string;
  total: number;
  percent: number;
  priceInCent: number;
  color?: string;
  onClick?: () => void;
}

export function ResidentAnalytic({
  title,
  total,
  icon,
  color,
  percent,
  priceInCent,
  onClick,
}: Props): JSX.Element {
  const { t } = useTranslation();
  const { fCurrencyCents, fNumber } = useFormatting();

  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="center"
      sx={{
        width: 1,
        minWidth: 200,
        ...(onClick && {
          cursor: 'pointer',
          '&:hover': {
            backgroundColor: 'grey.50008',
            borderRadius: 1,
          },
        }),
      }}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      aria-label={
        onClick
          ? `${title} ${fNumber(total)} ${t('common.receipts', {
              count: total,
            })}`
          : undefined
      }
      onKeyDown={(e) => {
        if (!onClick) {
          return;
        }
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      }}
    >
      <Stack
        alignItems="center"
        justifyContent="center"
        sx={{ position: 'relative' }}
      >
        {typeof icon === 'string' ? (
          <Iconify
            icon={icon}
            sx={{ color, width: 24, height: 24, position: 'absolute' }}
          />
        ) : (
          icon
        )}

        <CircularProgress
          variant="determinate"
          value={percent}
          size={56}
          thickness={4}
          sx={{ color, opacity: 0.48 }}
        />

        <CircularProgress
          variant="determinate"
          value={100}
          size={56}
          thickness={4}
          sx={{
            color: 'grey.50016',
            position: 'absolute',
            top: 0,
            left: 0,
            opacity: 0.48,
          }}
        />
      </Stack>

      <Stack spacing={0.5} sx={{ ml: 2 }}>
        <Typography
          variant="h6"
          sx={{
            fontSize: '1.125rem',
            fontWeight: 700,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {title}
        </Typography>

        <Typography variant="subtitle2">
          {fNumber(total)}{' '}
          <Box
            component="span"
            sx={{ color: 'text.secondary', typography: 'body2' }}
          >
            {t('common.receipts', { count: total })}
          </Box>
        </Typography>

        <Typography variant="subtitle2" sx={{ color }}>
          {fCurrencyCents(priceInCent)}
        </Typography>
      </Stack>
    </Stack>
  );
}
